import React, { useState, useEffect, useContext } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-final-form";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { UiButton } from "../../../_uiComponents/common/form/UiButton";
import OtpInput from "../../../_uiComponents/common/form/OtpInput";
import UiCheckbox from "../../../_uiComponents/common/form/UiCheckBox";
import { AuthWrapper } from "../../../_uiComponents/common/AuthWrapper";
import Validator from "../../../../_helpers/validate.schema";
import { otpTypeConstants, authConstants } from "../../../../_redux/_constants";
import { getFingerPrint } from "../../../../_helpers/support.services";
import {
     loginAuthRequest,
     reSendOtpRequest,
     forgotPasswordResetPasswordRequest,
} from "../../../../_redux/_actions";

const validateSchema = () =>
     yup.object().shape({
          otp: yup
               .string()
               .required("Otp is required.")
               .min(1, "Otp is required.")
               .min(6, "Six charactors are required."),
     });

const onValidate = async (values) => {
     return await Validator(values, validateSchema);
};

function OtpVerification() {
     const [remember, setRemember] = useState(false);
     const [seconds, setSeconds] = useState(30);

     const { id, userId } = useParams();

     const actionDispatch = useDispatch();
     const navigate = useNavigate();

     useEffect(() => {
          if (seconds > 0) {
               const timerId = setTimeout(() => {
                    setSeconds(seconds - 1);
               }, 1000);

               // Cleanup the timeout on component unmount
               return () => clearTimeout(timerId);
          }
     }, [seconds]);

     const { userProfileDetails } = useSelector((state) => {
          return {
               userProfileDetails: state.auth.userProfileDetails,
          };
     });

     const submit = async (values) => {
          if (id == otpTypeConstants.AUTH_TYPE_LOGIN) {
               const browserFingerprint = await getFingerPrint();
               actionDispatch(
                    loginAuthRequest({
                         email: userProfileDetails.email,
                         password: userProfileDetails.password,
                         browserFingerprint,
                         validateOtp: true,
                         otp: values.otp,
                         rememberBrowser: remember,
                         navigate,
                    }),
               );
          }

          if (id == otpTypeConstants.AUTH_TYPE_FOGOT_PASSWORD) {
               actionDispatch(
                    forgotPasswordResetPasswordRequest({
                         authToken: userProfileDetails.authToken,
                         confirmPassword: userProfileDetails.confirmPassword,
                         newPassword: userProfileDetails.newPassword,
                         userId: userProfileDetails.userId,
                         otp: values.otp,
                         validateOtp: true,
                         navigate,
                    }),
               );
          }
     };

     const resendOtp = async () => {
          const browserFingerprint = await getFingerPrint();

          actionDispatch(
               reSendOtpRequest({
                    userId: userId,
                    otpTypeId: id,
                    rememberBrowser: false,
                    browserFingerprint,
               }),
          );
     };

     return (
          <>
               <AuthWrapper heading="OTP Verification">
                    <Box component="div" sx={{ width: "100%" }}>
                         <div className="sba-auth-top-heading">
                              <h5>
                                   Please enter the OTP sent to your registered
                                   mobile & email
                              </h5>
                         </div>
                         <Form
                              onSubmit={submit}
                              validate={onValidate}
                              mutators={{
                                   setFormAttribute: (
                                        [fieldName, fieldVal],
                                        state,
                                        { changeValue },
                                   ) => {
                                        changeValue(
                                             state,
                                             fieldName,
                                             () => fieldVal,
                                        );
                                   },
                              }}
                              render={({
                                   form,
                                   handleSubmit,
                                   hasValidationErrors,
                                   values,
                                   ...formValues
                              }) => {
                                   return (
                                        <form onSubmit={validateSchema}>
                                             <OtpInput
                                                  otp={values.otp}
                                                  onChange={(e) => {
                                                       form.mutators.setFormAttribute(
                                                            "otp",
                                                            e,
                                                       );
                                                  }}
                                                  showValidation={
                                                       form.getState()
                                                            .submitFailed &&
                                                       formValues.errors.otp
                                                  }
                                                  validationMsg={
                                                       formValues.errors.otp
                                                  }
                                             />
                                             <Grid
                                                  container
                                                  sx={{
                                                       display: "flex",
                                                       alignItems: "center",
                                                       mb: 1,
                                                  }}
                                             >
                                                  <Grid
                                                       item
                                                       xs={10}
                                                       lg={8}
                                                       sx={{
                                                            textAlign: "left",
                                                       }}
                                                  >
                                                       <UiCheckbox
                                                            checked={remember}
                                                            label="Remember this device for 30 days"
                                                            handleChange={(
                                                                 e,
                                                            ) => {
                                                                 setRemember(
                                                                      !remember,
                                                                 );
                                                            }}
                                                       />
                                                  </Grid>
                                                  <Grid
                                                       item
                                                       xs={2}
                                                       lg={4}
                                                       className="sba-otp-checkbox"
                                                       sx={{
                                                            textAlign: "left",
                                                            display: "flex",
                                                            cursor: "pointer",
                                                       }}
                                                  >
                                                       <Tooltip
                                                            title="If you check this, 
                                                            OTP verification won't be required while logging in for
                                                            the next 30 days."
                                                            placement="top"
                                                            arrow
                                                       >
                                                            <InfoIcon className="sba-info-icon" />
                                                       </Tooltip>
                                                  </Grid>
                                             </Grid>

                                             <Grid container sx={{ mb: 6 }}>
                                                  <Grid
                                                       alignItems="center"
                                                       display="flex"
                                                       item
                                                       xs={6}
                                                       sx={{
                                                            textAlign: "left",
                                                       }}
                                                  >
                                                       <span
                                                            style={
                                                                 seconds == 0
                                                                 ? {}
                                                                 : {
                                                                           pointerEvents: "none",
                                                                           color: "#c6c7c9",
                                                                      }
                                                            }
                                                            variant="body2"
                                                            className="sba-forgot-password-link"
                                                            onClick={() => {
                                                                 if (
                                                                      seconds ==
                                                                      0
                                                                 ) {
                                                                      setSeconds(
                                                                           30,
                                                                      );
                                                                      resendOtp();
                                                                 }
                                                            }}
                                                       >
                                                            Resend OTP
                                                       </span>
                                                       <div className="sb-otp-timer">
                                                            ({seconds})
                                                       </div>
                                                  </Grid>
                                                  <Grid
                                                       item
                                                       xs={6}
                                                       sx={{
                                                            textAlign: "right",
                                                       }}
                                                  >
                                                       <UiButton
                                                            name="Verify"
                                                            variant="contained"
                                                            size="large"
                                                            onClick={
                                                                 handleSubmit
                                                            }
                                                       />
                                                  </Grid>
                                             </Grid>
                                        </form>
                                   );
                              }}
                         />
                         <p className="sba-login-bottom-support-mail">
                              Please reach{" "}
                              <span>support@sherbetrewards.com </span>
                              for further queries
                         </p>
                    </Box>
               </AuthWrapper>
          </>
     );
}

export default OtpVerification;
